/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: PhotoV2 辅助函数
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2025-11-30 16:57:26
 */
import type { FilterState } from '../components/PhotoFilter/types'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import CONFIG from '../../../constants/config'
import { DepartmentMemberV2, DepartmentV2 } from '../../../global'

// 定义 PlainQueryFilter 类型（从 photo/lib/photo.ts 复制）
type PlainQueryFilter = {
  departments: DepartmentV2[]
  departmentUsers: DepartmentMemberV2[]
  members: string[]
  searchKeywords: string[]
  mediaType: number | null
  sourceType: number | null
  times: [Dayjs, Dayjs]
  watermarks: string[]
  locations: string[]
  excludeUserIDs?: string[]
  useSynonym: boolean
  useSegment: boolean
  useSuggestedSearch: boolean
  suggestedSearchWords: {
    word: string
    source: string
    isChecked: boolean
  }[]
  existWatermark: number
  labelIDs?: string[]
  labelMatchAll?: boolean
}

type LocationRecommend = {
  word: string
  key: string
}

type BuildParamsOptions = {
  queryFilter: PlainQueryFilter
  groupID: string
  lastPageCond?: string
  locationRecommend?: LocationRecommend | null
  byLocation?: boolean
  isPreciseSearch?: boolean
  excludeKeywords?: string[]
  isAscSort?: boolean
}

/**
 * 将 FilterState 转换为 PlainQueryFilter
 */
export const convertFilterStateToPlainQueryFilter = (
  filter: FilterState
): PlainQueryFilter => {
  // 处理 times，确保是 [Dayjs, Dayjs] 格式
  // 如果 times 为 null，使用默认值（最近6个月）
  let times: [Dayjs, Dayjs] = [
    dayjs().subtract(6, 'months').startOf('day'),
    dayjs().endOf('day')
  ]
  if (filter.times && filter.times[0] && filter.times[1]) {
    times = filter.times
  }

  // 处理 locations，PlainQueryFilter 期望的是字符串数组
  let locations: string[] = []
  if (filter.locations && filter.locations.length > 0) {
    locations = filter.locations.map((loc) => loc.name || loc.detail || '')
  }

  return {
    departments: filter.departments || [],
    departmentUsers: filter.departmentUsers || [],
    members: [], // PlainQueryFilter 需要，但 FilterState 没有，设为空数组
    searchKeywords: filter.searchKeywords || [],
    mediaType: filter.mediaType ?? null,
    sourceType: filter.sourceType ?? null,
    times,
    watermarks: filter.watermarks || [],
    locations,
    excludeUserIDs: filter.excludeUserIDs || [],
    useSynonym: filter.useSynonym ?? true,
    useSegment: filter.useSegment ?? true,
    useSuggestedSearch: filter.useSuggestedSearch ?? true,
    suggestedSearchWords: (filter.suggestedSearchWords || []).map((word) => ({
      word: typeof word === 'string' ? word : word.word,
      source: typeof word === 'string' ? '' : (word as any).source || '',
      isChecked: typeof word === 'string' ? false : word.isChecked || false
    })),
    existWatermark: filter.existWatermark ?? 0,
    labelIDs: filter.labelIDs || [],
    labelMatchAll: filter.labelMatchAll ?? false
  }
}

const buildParamsFromPlain = (options: BuildParamsOptions) => {
  const {
    queryFilter,
    groupID,
    lastPageCond = '',
    locationRecommend = null,
    byLocation = false,
    isPreciseSearch = false,
    excludeKeywords = [],
    isAscSort = false
  } = options
  const departmentIDList = queryFilter.departments.map(
    (department) => department.id
  )
  const userIDList = queryFilter.departmentUsers.map((user) => user.userID)

  return {
    filter: {
      searchKeywords: byLocation ? [] : queryFilter.searchKeywords,
      mediaType: queryFilter.mediaType,
      sourceType: queryFilter.sourceType,
      times: [
        {
          start: (queryFilter.times[0] as Dayjs).format(CONFIG.timeFormat),
          end: (queryFilter.times[1] as Dayjs).format(CONFIG.timeFormat)
        }
      ],
      userIDList,
      excludeUserIDList: queryFilter.excludeUserIDs || [],
      departmentIDList,
      watermarks: queryFilter.watermarks.map((watermark) => ({
        watermarkBaseID: watermark.split('+')[0],
        groupWatermarkID: watermark.split('+')[1]
      })),
      locations: queryFilter.locations,
      useSegment: queryFilter.useSegment,
      useSynonym: false,
      useSuggestedSearch: byLocation ? false : true,
      suggestedSearchWords: byLocation ? [] : queryFilter.suggestedSearchWords,
      // locationRecommend 取值优先级:
      //   1. 显式传入(AI 照片君按地点跳转 / 主页地点关键词搜索)
      //   2. byLocation=true 时退化用 searchKeywords[0] 当 word(老链路)
      //   3. 都没有则空对象,后端 prepareSearchFilter 读到空 → photosearch 不按 LocationKey 过滤
      locationRecommend:
        locationRecommend ||
        (byLocation
          ? { word: queryFilter.searchKeywords[0] || '', key: '' }
          : {}),
      excludeKeywords,
      existWatermark: queryFilter.existWatermark,
      labelIDs: queryFilter.labelIDs,
      labelMatchAll: queryFilter.labelMatchAll
    },
    isPreciseSearch,
    isAscSort,
    groupID,
    lastPageCond
  }
}

/**
 * 构建获取照片参数（适配 FilterState）
 */
export const buildFetchPhotosParamsWithOptions = (options: {
  queryFilter: FilterState
  groupID: string
  lastPageCond?: string
  locationRecommend?: LocationRecommend | null
  byLocation?: boolean
  isPreciseSearch?: boolean
  excludeKeywords?: string[]
  isAscSort?: boolean
}) => {
  const plainQueryFilter = convertFilterStateToPlainQueryFilter(
    options.queryFilter
  )
  const params = buildParamsFromPlain({
    ...options,
    queryFilter: plainQueryFilter,
    lastPageCond: options.lastPageCond || '',
    // excludeKeywords 合并两个来源:filter state 里的(AI 照片君"排除 X" / 主页排除词)
    // + options 传入的(精准搜索的 notWords)。convertFilterStateToPlainQueryFilter
    // 不携带 excludeKeywords,这里从原始 queryFilter 取并去重后传给 buildParamsFromPlain。
    excludeKeywords: Array.from(
      new Set([
        ...(options.queryFilter.excludeKeywords || []),
        ...(options.excludeKeywords || [])
      ])
    )
  }) as any

  if (
    options.queryFilter.locations &&
    options.queryFilter.locations.length > 0
  ) {
    params.filter.locations = options.queryFilter.locations
  }

  return params
}

/**
 * 计算选中的成员数量（FilterState）
 * 计算选中的成员数量（FilterState）
 */
export const calcPickedMemberNum = (
  filter: FilterState | null | undefined,
  members: { size?: number; length?: number } | null
): number => {
  const membersSize =
    typeof members?.size === 'number'
      ? members.size
      : typeof members?.length === 'number'
        ? members.length
        : 0

  if (!filter) {
    return membersSize
  }

  const departments = filter.departments || []
  const departmentUsers = filter.departmentUsers || []

  const userNumInDepartments = departments.reduce((acc, department) => {
    const num =
      typeof department?.num === 'number'
        ? department.num
        : parseInt((department as any)?.num ?? '0', 10)
    return acc + (Number.isFinite(num) ? num : 0)
  }, 0)

  const total = departmentUsers.length + userNumInDepartments

  const hasAllDepartmentSelected = departments.some(
    (department) => department?.id === '0'
  )

  if (total === 0 || total === membersSize || hasAllDepartmentSelected) {
    return membersSize
  }

  return total
}

/**
 * 格式化标题数据（合并相同标题名称的标题ID）
 */
export const formatTitlesData = (titlesData: any[]): any[] => {
  return titlesData.reduce((prev, cur) => {
    for (const item of prev) {
      if (cur.titleName === item.titleName) {
        item.titleId = item.titleId + ',' + cur.titleId
        return prev
      }
    }
    prev.push(cur)
    return prev
  }, [])
}

/**
 * 照片归类方式分类（用于埋点）
 */
export const sensorsClassification = (folderRule: string): string => {
  return folderRule === 'customize'
    ? 'custom'
    : folderRule === 'date'
      ? 'date'
      : folderRule === 'user'
        ? 'member'
        : 'notClassification'
}

/**
 * 照片命名方式（用于埋点）
 */
export const sensorsNamingMethod = (namingRule: string[]): string[] => {
  return namingRule.map((rule) => {
    if (rule.includes('title')) {
      return rule.split('title:')[1]
    } else if (rule.includes('diy')) {
      return '自定义标签'
    } else {
      switch (rule) {
        case 'user':
          return '拍摄人姓名'
        case 'time':
          return '拍摄时间'
        case 'address':
          return '拍摄地点'
        case 'watermark':
          return '水印模板'
        case 'photoNumber':
          return '照片编号'
        default:
          return ''
      }
    }
  })
}
